import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Users,
  Plus,
  Search,
  MoreHorizontal,
  Edit2,
  Trash2,
  Eye,
  Loader2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { getErrorMessage } from "@/lib/utils";

export default function Members() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    member_id: "",
    member_status: "active",
    location_ids: [],
    expiration_date: ""
  });

  const fetchData = async () => {
    try {
      const [membersRes, locationsRes] = await Promise.all([
        axios.get(`${API}/members`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: search || undefined,
            member_status: statusFilter !== "all" ? statusFilter : undefined,
            location_id: locationFilter !== "all" ? locationFilter : undefined
          }
        }),
        axios.get(`${API}/locations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMembers(membersRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, search, statusFilter, locationFilter]);

  const handleOpenDialog = (member = null) => {
    if (member) {
      setSelectedMember(member);
      // Format expiration date for the date input (YYYY-MM-DD)
      let expirationForInput = "";
      if (member.expiration_date) {
        expirationForInput = member.expiration_date.split("T")[0];
      }
      setFormData({
        name: member.name,
        member_id: member.member_id,
        member_status: member.status,
        location_ids: member.location_ids || [],
        expiration_date: expirationForInput
      });
    } else {
      setSelectedMember(null);
      setFormData({
        name: "",
        member_id: "",
        member_status: "active",
        location_ids: [],
        expiration_date: ""
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.member_id) {
      toast.error("Name and Member ID are required");
      return;
    }

    setSaving(true);
    try {
      if (selectedMember) {
        // When editing, include expiration_date if set
        const updateData = {
          ...formData,
          expiration_date: formData.expiration_date 
            ? new Date(formData.expiration_date + "T23:59:59Z").toISOString()
            : null
        };
        await axios.put(`${API}/members/${selectedMember.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Member updated successfully");
      } else {
        // When creating, don't send expiration_date (it's auto-calculated from location)
        const { expiration_date, ...createData } = formData;
        await axios.post(`${API}/members`, createData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Member created successfully");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving member:", error);
      toast.error(getErrorMessage(error, "Failed to save member"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMember) return;

    setSaving(true);
    try {
      await axios.delete(`${API}/members/${selectedMember.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Member deleted successfully");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error deleting member:", error);
      toast.error("Failed to delete member");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "badge-active",
      suspended: "badge-suspended",
      expired: "badge-expired"
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.expired}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="members-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Members</h1>
          <p className="text-gray-500 mt-1">
            {user?.role === "staff" ? "View and add members" : "Manage your membership database"}
          </p>
        </div>
        {/* All users can add members */}
        <Button onClick={() => handleOpenDialog()} className="btn-press" data-testid="add-member-btn">
          <Plus className="mr-2 h-4 w-4" />
          Add Member
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by name or member ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="location-filter">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Member ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Last Check-in</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.length > 0 ? (
                members.map((member) => {
                  const isExpired = member.expiration_date && new Date(member.expiration_date) < new Date();
                  return (
                  <TableRow key={member.id} className="table-row-hover" data-testid={`member-row-${member.id}`}>
                    <TableCell className="font-medium">
                      {member.name}
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {member.member_id}
                      </code>
                    </TableCell>
                    <TableCell>{getStatusBadge(member.status)}</TableCell>
                    <TableCell>
                      {member.expiration_date ? (
                        <span className={isExpired ? "text-red-600 font-medium" : "text-gray-600"}>
                          {format(parseISO(member.expiration_date), "MMM d, yyyy")}
                          {isExpired && " (Expired)"}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {member.location_ids?.length > 0
                        ? member.location_ids.map(id => getLocationName(id)).join(", ")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {member.last_check_in
                        ? format(parseISO(member.last_check_in), "MMM d, yyyy h:mm a")
                        : "Never"}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`member-actions-${member.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/members/${member.id}`)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          {/* Staff cannot edit or delete members */}
                          {user?.role !== "staff" && (
                            <>
                              <DropdownMenuItem onClick={() => handleOpenDialog(member)}>
                                <Edit2 className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => {
                                  setSelectedMember(member);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">No members found</p>
                    <Button variant="link" onClick={() => handleOpenDialog()} className="mt-2">
                      Add your first member
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{selectedMember ? "Edit Member" : "Add Member"}</DialogTitle>
            <DialogDescription>
              {selectedMember ? "Update member information" : "Add a new member to your database"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                data-testid="member-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="member_id">Member ID *</Label>
              <Input
                id="member_id"
                value={formData.member_id}
                onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                placeholder="Alphanumeric ID (e.g., MEM001)"
                data-testid="member-id-input"
              />
              <p className="text-xs text-gray-500">
                Unique identifier for this member
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.member_status}
                onValueChange={(value) => setFormData({ ...formData, member_status: value })}
              >
                <SelectTrigger data-testid="member-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={formData.location_ids[0] || ""}
                onValueChange={(value) => setFormData({ ...formData, location_ids: value ? [value] : [] })}
              >
                <SelectTrigger data-testid="member-location-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Expiration Date - only show when editing */}
            {selectedMember && (
              <div className="space-y-2">
                <Label htmlFor="expiration_date">Membership Expiration</Label>
                <Input
                  id="expiration_date"
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                  data-testid="member-expiration-input"
                />
                <p className="text-xs text-gray-500">
                  Member will not be able to check in after this date
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-member-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {selectedMember ? "Save Changes" : "Add Member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedMember?.name}? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
